import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Download, Send, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agency, setAgency] = useState('');
  const [contractType, setContractType] = useState('');
  const [projectNeed, setProjectNeed] = useState('');

  const contractOptions = [
    { value: 'prime', label: 'Prime Contract' },
    { value: 'subcontract', label: 'Subcontract' },
    { value: 'joint-venture', label: 'Joint Venture' },
    { value: 'task-order', label: 'Task Order' },
    { value: 'bpa', label: 'BPA / IDIQ' },
    { value: 'other', label: 'Other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !agency || !contractType || !projectNeed) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name,
        email,
        agency,
        contract_type: contractType,
        project_need: projectNeed,
      });
      toast.success(res.data.message);
      setName('');
      setEmail('');
      setAgency('');
      setContractType('');
      setProjectNeed('');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden"
    >
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at 30% 50%, rgba(184,16,46,0.06) 0%, transparent 50%)'
      }} />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-body text-[#B8102E] text-xs tracking-[0.3em] uppercase mb-4">
              Let's Connect
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              Step Into Any Position.{' '}
              <span className="text-[#B8102E]">Deliver Every Time.</span>
            </h2>
            <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">
              Whether you need a strategic partner for a new initiative or execution
              support for an existing program, Cole Utilities is ready to deliver.
            </p>
            <Button
              data-testid="download-capability-btn"
              variant="outline"
              asChild
              className="border-[#2A2A2A] text-white hover:border-[#B8102E] hover:text-[#B8102E] font-body font-medium text-sm px-6 h-11 rounded-full transition-all duration-300 bg-transparent"
            >
              <a href="/capability-statement.pdf" download>
                <Download className="mr-2 h-4 w-4" />
                Download Capability Statement
              </a>
            </Button>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-body text-xs text-[#737373] uppercase tracking-wider mb-2 block">
                    Full Name
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    className="bg-[#141414] border-[#2A2A2A] text-white placeholder:text-[#535353] focus:border-[#B8102E] focus-visible:ring-[#B8102E] h-11"
                  />
                </div>
                <div>
                  <label className="font-body text-xs text-[#737373] uppercase tracking-wider mb-2 block">
                    Email
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@agency.gov"
                    className="bg-[#141414] border-[#2A2A2A] text-white placeholder:text-[#535353] focus:border-[#B8102E] focus-visible:ring-[#B8102E] h-11"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs text-[#737373] uppercase tracking-wider mb-2 block">
                  Agency / Company
                </label>
                <Input
                  data-testid="contact-agency-input"
                  value={agency}
                  onChange={(e) => setAgency(e.target.value)}
                  placeholder="Department of Defense"
                  className="bg-[#141414] border-[#2A2A2A] text-white placeholder:text-[#535353] focus:border-[#B8102E] focus-visible:ring-[#B8102E] h-11"
                />
              </div>

              <div>
                <label className="font-body text-xs text-[#737373] uppercase tracking-wider mb-2 block">
                  Contract Type
                </label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="contact-contract-select"
                      className="flex h-11 w-full items-center justify-between rounded-md border border-[#2A2A2A] bg-[#141414] px-3 py-2 text-sm text-white focus:border-[#B8102E] focus:outline-none focus:ring-1 focus:ring-[#B8102E]"
                    >
                      <span className={contractType ? 'text-white' : 'text-[#535353]'}>
                        {contractType
                          ? contractOptions.find(o => o.value === contractType)?.label || contractType
                          : 'Select contract type'}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#141414] border-[#2A2A2A] w-[var(--radix-dropdown-menu-trigger-width)]">
                    {contractOptions.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        data-testid={`contract-option-${opt.value}`}
                        onSelect={() => setContractType(opt.value)}
                        className="text-white hover:bg-[#B8102E]/10 focus:bg-[#B8102E]/10 cursor-pointer"
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div>
                <label className="font-body text-xs text-[#737373] uppercase tracking-wider mb-2 block">
                  Project Need
                </label>
                <Textarea
                  data-testid="contact-project-input"
                  value={projectNeed}
                  onChange={(e) => setProjectNeed(e.target.value)}
                  placeholder="Describe your project requirements..."
                  rows={4}
                  className="bg-[#141414] border-[#2A2A2A] text-white placeholder:text-[#535353] focus:border-[#B8102E] focus-visible:ring-[#B8102E]"
                />
              </div>

              <Button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-[#B8102E] text-white hover:bg-[#97021C] font-body font-semibold text-sm h-12 rounded-full transition-all duration-300 btn-sweep"
              >
                {loading ? 'Sending...' : 'Start a Conversation'}
                <Send className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
